/**
 * usePledges Hook
 *
 * Fetches and manages pledge data for the authenticated donor
 */

import { useState, useEffect, useCallback } from 'react';
import { getPledges } from '@/services/api/pledgeService';
import type { Pledge, GetPledgesOptions } from '@/services/api/pledgeService';

export interface UsePledgesOptions extends GetPledgesOptions {
  companyId?: number;
  autoFetch?: boolean;
}

export interface UsePledgesResult {
  pledges: Pledge[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  totalAmount: number;
  pendingCount: number;
  pendingAmount: number;
  confirmedCount: number;
  confirmedAmount: number;
}

export function usePledges(
  companyId: number | undefined,
  options: UsePledgesOptions = {}
): UsePledgesResult {
  const { autoFetch = true, ...fetchOptions } = options;

  const [pledges, setPledges] = useState<Pledge[]>([]);
  const [isLoading, setIsLoading] = useState(autoFetch);
  const [error, setError] = useState<Error | null>(null);

  const fetchPledges = useCallback(async () => {
    if (!companyId) {
      setPledges([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const data = await getPledges(companyId, fetchOptions);
      setPledges(data.items);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch pledges'));
    } finally {
      setIsLoading(false);
    }
  }, [companyId, fetchOptions.donorId, fetchOptions.statusCode]);

  useEffect(() => {
    if (autoFetch) fetchPledges();
  }, [autoFetch, fetchPledges]);

  // Computed stats
  const totalAmount = pledges.reduce((sum, p) => sum + (Number(p.amountPledged) ?? 0), 0);
  const pending = pledges.filter((p) => p.statusCode === 1);
  const confirmed = pledges.filter((p) => p.statusCode === 2);

  return {
    pledges,
    isLoading,
    error,
    refetch: fetchPledges,
    totalAmount,
    pendingCount: pending.length,
    pendingAmount: pending.reduce((s, p) => s + (Number(p.amountPledged) ?? 0), 0),
    confirmedCount: confirmed.length,
    confirmedAmount: confirmed.reduce((s, p) => s + (Number(p.amountPledged) ?? 0), 0),
  };
}
